import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ApiService } from '../api/api.service';

export interface XrefApiKeyMasked {
  value: string;
  created_at: string | null;
  last_used: string | null;
}

export interface XrefApiKeyPlaintext {
  value: string;
}

export interface XrefApiWebhookInfo {
  url: string;
  secret?: string | null;
}

interface OmniAppResponse<T> {
  data: T;
  status_message: string;
  message: string | null;
}

const API_KEY_PATH = 'organisation/xref-api/api-key';
const WEBHOOK_PATH = 'organisation/xref-api/webhook';

@Injectable({
  providedIn: 'root',
})
export class XrefApiService {
  constructor(private api: ApiService) {}

  getKey(): Observable<XrefApiKeyMasked | null> {
    return this.api.get(API_KEY_PATH).pipe(
      map((res: OmniAppResponse<XrefApiKeyMasked>) => res.data),
      catchError((err: HttpErrorResponse) => {
        if (err && err.status === 404) {
          return of(null);
        }
        return throwError(() => err);
      }),
    );
  }

  generateKey(): Observable<XrefApiKeyPlaintext> {
    return this.api
      .post(API_KEY_PATH, {})
      .pipe(map((res: OmniAppResponse<XrefApiKeyPlaintext>) => res.data));
  }

  revokeKey(): Observable<void> {
    return this.api.delete(API_KEY_PATH).pipe(map(() => undefined));
  }

  getWebhook(): Observable<XrefApiWebhookInfo | null> {
    return this.api.get(WEBHOOK_PATH).pipe(
      map((res: OmniAppResponse<XrefApiWebhookInfo>) => res.data),
      catchError((err: HttpErrorResponse) => {
        if (err && err.status === 404) {
          return of(null);
        }
        return throwError(() => err);
      }),
    );
  }

  saveWebhook(url: string): Observable<XrefApiWebhookInfo> {
    return this.api
      .put(WEBHOOK_PATH, { url })
      .pipe(map((res: OmniAppResponse<XrefApiWebhookInfo>) => res.data));
  }

  deleteWebhook(): Observable<void> {
    return this.api.delete(WEBHOOK_PATH).pipe(map(() => undefined));
  }
}
